import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { themeService, type ThemeData } from "@/services/themeService";
import { toast } from "sonner";
import { useCallback, useEffect } from "react";

const themeKeys = {
  all: ["themes"] as const,
  list: () => [...themeKeys.all, "list"] as const,
};

export function useThemesList() {
  return useQuery({
    queryKey: themeKeys.list(),
    queryFn: () => themeService.listThemes(),
    staleTime: 10 * 60 * 1000,
  });
}

export function useSetMyTheme() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (themeId: string) => themeService.setMyTheme(themeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: themeKeys.list() });
      toast.success("Tema atualizado!", {
        description: "Suas preferências visuais foram salvas.",
      });
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || "Erro ao atualizar tema";
      toast.error("Erro ao salvar", { description: message });
    },
  });
}

/**
 * Apply a theme's CSS variables to the document root element.
 * This overrides the default CSS variables defined in globals.css.
 */
export function applyThemeVariables(cssVariables: Record<string, string>) {
  const root = document.documentElement;
  for (const [key, value] of Object.entries(cssVariables)) {
    root.style.setProperty(key, value);
  }
}

/**
 * Clear all inline theme variables (revert to globals.css defaults)
 */
export function clearThemeVariables(cssVariables: Record<string, string>) {
  const root = document.documentElement;
  for (const key of Object.keys(cssVariables)) {
    root.style.removeProperty(key);
  }
}

/**
 * Hook that applies the active theme's CSS variables.
 * Call this once in the app layout to keep the theme in sync.
 */
export function useApplyActiveTheme(activeTheme: ThemeData | null | undefined) {
  useEffect(() => {
    if (!activeTheme?.css_variables) return;
    applyThemeVariables(activeTheme.css_variables);

    return () => {
      if (activeTheme?.css_variables) {
        clearThemeVariables(activeTheme.css_variables);
      }
    };
  }, [activeTheme]);
}

/**
 * Preview a theme temporarily (hover/click) without persisting
 */
export function useThemePreview() {
  const preview = useCallback((theme: ThemeData) => {
    applyThemeVariables(theme.css_variables);
  }, []);

  const cancelPreview = useCallback((previousTheme: ThemeData | null) => {
    if (previousTheme) {
      applyThemeVariables(previousTheme.css_variables);
    }
  }, []);

  return { preview, cancelPreview };
}
